import { Check, LoaderCircle, RefreshCw, WifiOff } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BUILD } from '@/config/build';
import { buildLabel } from '@/lib/build-label';
import { checkForUpdate, useUpdate, useUpdateStatus } from '@/lib/pwa';
import { cn } from '@/lib/cn';

const linkButton = 'inline-flex items-center gap-1.5 rounded-md font-medium underline-offset-4 transition-colors hover:underline disabled:pointer-events-none';

/**
 * The running version and where it stands with updates, at the foot of the
 * home page: on a phone, an installed app gives no other sign that it updated.
 */
export function AppVersion({ className }: { readonly className?: string }) {
  const { t, i18n } = useTranslation();
  const status = useUpdateStatus();
  const apply = useUpdate();
  const [applying, setApplying] = useState(false);

  return (
    <div className={cn('flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[13px] text-fg-subtle', className)}>
      <span className="tabular">{t('update.version', { build: buildLabel(BUILD, i18n.language) })}</span>
      {status !== 'unsupported' && <span aria-hidden>·</span>}
      <span aria-live="polite" className="inline-flex items-center">
        {status === 'unchecked' && (
          <button type="button" className={cn(linkButton, 'text-fg-muted hover:text-fg')} onClick={() => void checkForUpdate()}>
            {t('update.check')}
          </button>
        )}
        {status === 'checking' && (
          <span className="inline-flex items-center gap-1.5">
            <LoaderCircle size={13} className="animate-spin text-accent-text" />
            {t('update.checking')}
          </span>
        )}
        {status === 'current' && (
          // Still a button: someone who just published an update wants to look again.
          <button type="button" title={t('update.check')} className={cn(linkButton, 'text-fg-muted hover:text-fg')} onClick={() => void checkForUpdate()}>
            <Check size={13} strokeWidth={2.6} className="text-accent-text" />
            {t('update.current')}
          </button>
        )}
        {status === 'offline' && (
          <button type="button" className={cn(linkButton, 'text-fg-muted hover:text-fg')} onClick={() => void checkForUpdate()}>
            <WifiOff size={13} />
            {t('update.offline')}
          </button>
        )}
        {status === 'ready' && apply && (
          <button
            type="button"
            disabled={applying}
            className={cn(linkButton, 'text-accent-text')}
            onClick={() => {
              setApplying(true);
              void apply().catch(() => setApplying(false));
            }}
          >
            <RefreshCw size={13} className={applying ? 'animate-spin' : undefined} />
            {t('update.ready')} {t('update.apply')}
          </button>
        )}
      </span>
    </div>
  );
}
